import { useEffect, useMemo, useState } from 'react'

import { API } from '@/api'
import type { GetTeamResourceACLRangesRes } from '@/api/type-response'
import { useTeamStore } from '@/stores/team'

type State =
  | { loading: true; data: null; error?: unknown }
  | { loading: false; data: GetTeamResourceACLRangesRes['aclRanges']['options']; error?: unknown }

export function useAclRanges(teamResIDFromProp?: string) {
  const fallbackTeamResID = useTeamStore((s) => s.teamTokenData?.teamResID) ?? ''
  const teamResID = teamResIDFromProp ?? fallbackTeamResID

  const [state, setState] = useState<State>({ loading: true, data: null })

  useEffect(() => {
    let cancelled = false
    async function run() {
      try {
        setState({ loading: true, data: null })
        const res = await API.getTeamResourceACLRanges({ res_id: teamResID })
        const options = res?.aclRanges?.options
        if (!cancelled) {
          setState({ loading: false, data: options })
        }
      } catch (err) {
        if (!cancelled)
          setState({
            loading: false,
            data: {
              regionRanges: { supported: true, publicRegions: [], privateRegions: [] },
              machineOptions: {
                supported: true,
                machineCategories: [0, 1, 2],
                machineGPUModels: [],
                machineCPUModels: [],
                machineVGPUModels: [],
              },
              imageOptions: { supported: true },
            } as GetTeamResourceACLRangesRes['aclRanges']['options'],
            error: err,
          })
      }
    }
    if (teamResID) run()
    return () => {
      cancelled = true
    }
  }, [teamResID])

  const regions = useMemo(() => {
    if (!state.data) return []
    const pub = state.data.regionRanges?.publicRegions ?? []
    const pri = state.data.regionRanges?.privateRegions ?? []
    return [
      ...pub.map((r) => ({ label: r.displayName, value: r.domain, isPrivate: false })),
      ...pri.map((r) => ({ label: r.displayName, value: r.domain, isPrivate: true })),
    ]
  }, [state.data])

  const machine = state.data?.machineOptions

  return {
    loading: state.loading,
    regions,
    machineCategories: machine?.machineCategories ?? [0, 1, 2],
    machineGPUModels: machine?.machineGPUModels ?? [],
    machineCPUModels: machine?.machineCPUModels ?? [],
    machineVGPUModels: machine?.machineVGPUModels ?? [],
    imageOptionsSupported: state.data?.imageOptions?.supported ?? true,
    raw: state.data,
    error: state.error,
  }
}
